import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  ContinueTextRequest,
  CritiqueTextRequest,
  DistillTextRequest,
  ExecuteTextOperationRequest,
  ExpandTextRequest,
  GenerateTextRequest,
  RefineTextRequest,
  RestructureTextRequest,
  StylizeTextRequest,
  TextOperationResult,
  TextOperationStatus,
} from '../domain';

const BASE = '/api/v3/text/operations';

const TERMINAL: readonly TextOperationStatus[] = ['Completed', 'Failed', 'Cancelled'];

/**
 * Writer's Lab text operations. Mirrors OperationService — one method per BE
 * endpoint, each unwrapping the ApiResponse envelope via callV3. `continue_`
 * is suffixed because `continue` is a reserved word.
 */
export const TextOperationService = {
  execute: (request: ExecuteTextOperationRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/execute`, request),
    ),

  generate: (request: GenerateTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/generate`, request),
    ),

  continue_: (request: ContinueTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/continue`, request),
    ),

  expand: (request: ExpandTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/expand`, request),
    ),

  refine: (request: RefineTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/refine`, request),
    ),

  restructure: (request: RestructureTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/restructure`, request),
    ),

  stylize: (request: StylizeTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/stylize`, request),
    ),

  critique: (request: CritiqueTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/critique`, request),
    ),

  distill: (request: DistillTextRequest) =>
    callV3<TextOperationResult>(
      api.post<ApiResponse<TextOperationResult>>(`${BASE}/distill`, request),
    ),

  /** Polling endpoint — reconstructs a Completed result from the persisted draft. */
  getStatus: (operationId: string) =>
    callV3<TextOperationResult>(
      api.get<ApiResponse<TextOperationResult>>(`${BASE}/jobs/${operationId}`),
    ),

  async pollStatus(
    operationId: string,
    onProgress?: (result: TextOperationResult) => void,
    maxAttempts = 120,
    intervalMs = 2000,
  ): Promise<TextOperationResult> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = await this.getStatus(operationId);
      onProgress?.(result);
      if (TERMINAL.includes(result.status)) {
        return result;
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error(
      `Text operation '${operationId}' polling timed out after ${maxAttempts * intervalMs}ms`,
    );
  },
};
